import { criterion, fail, fromCriteria, pass, unsupported } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readBookmarks } from "@/office/word/einfuegen";
import { readFields } from "@/office/word/layout";

export const lektionTextmarken: Lesson = {
  id: "textmarken",
  title: "Textmarken und Verweise darauf",
  summary: "Eine Stelle benennen, damit Links und Querverweise sie wiederfinden.",
  durationMinutes: 8,
  learningGoals: [
    "Eine Textmarke setzen und benennen",
    "Mit „Gehe zu“ und Links zu einer Textmarke springen",
    "Einen Querverweis auf eine Textmarke einfügen",
  ],
  steps: [
    {
      id: "textmarke-setzen",
      title: "Eine Textmarke setzen",
      explanation: [
        "Eine Textmarke ist ein benannter Ort im Dokument – ein Lesezeichen. Überschriften " +
          "sind über ihre Formatvorlage ohnehin ansteuerbar; für alles andere, etwa eine " +
          "bestimmte Zahl, eine Definition oder eine Tabelle, gibt es Textmarken.",
        "Stelle markieren, „Einfügen“ → „Textmarke“, einen Namen vergeben, „Hinzufügen“. " +
          "Der Name beginnt mit einem Buchstaben und enthält keine Leerzeichen – " +
          "„Gesamtbudget“ oder „Tabelle_Kosten“.",
        "Sichtbar werden Textmarken über „Datei“ → „Optionen“ → „Erweitert“ → " +
          "„Textmarken anzeigen“ als graue Klammern. Mit F5 („Gehe zu“) springst du direkt " +
          "zu einer Textmarke.",
      ],
      keyPoints: [
        "Einfügen → Textmarke → Name → Hinzufügen.",
        "Namen ohne Leerzeichen, mit Buchstabe am Anfang.",
        "F5 → Gehe zu → Textmarke.",
      ],
      task: {
        instruction: "Setze eine Textmarke mit einem sprechenden Namen, zum Beispiel „Gesamtbudget“.",
        hint: "Ein Wort oder eine Zahl markieren, Einfügen → Textmarke.",
        check: async () => {
          const marken = await readBookmarks();
          if (marken === null) {
            return unsupported(
              "Diese Word-Version stellt dem Add-in keine Textmarken zur Verfügung (ab WordApi 1.4).",
              "Prüfe unter Einfügen → Textmarke, ob deine Marke in der Liste steht. Danach kannst du den Schritt selbst als erledigt markieren.",
            );
          }
          return marken.length > 0
            ? pass(`Die Stelle hat jetzt einen Namen: ${marken.join(", ")}.`)
            : fail(
                "Im Dokument ist noch keine Textmarke.",
                "Textmarken, deren Name mit einem Unterstrich beginnt, legt Word selbst an – sie zählen nicht.",
              );
        },
      },
    },
    {
      id: "verweis-auf-textmarke",
      title: "Ein Querverweis auf die Textmarke",
      explanation: [
        "Ein Querverweis holt den Inhalt oder die Seitenzahl einer Textmarke an eine " +
          "andere Stelle – „siehe Gesamtbudget auf Seite 4“. Ändert sich der Betrag oder " +
          "verschiebt sich die Seite, zieht der Verweis beim Aktualisieren (F9) nach.",
        "„Einfügen“ → „Querverweis“ (oder „Verweise“ → „Querverweis“), bei „Verweistyp“ " +
          "„Textmarke“ wählen, bei „Verweisen auf“ „Textmarkeninhalt“ oder „Seitenzahl“. " +
          "Das Häkchen „Als Hyperlink einfügen“ macht den Verweis zusätzlich anklickbar.",
      ],
      keyPoints: [
        "Verweistyp „Textmarke“ – Inhalt oder Seitenzahl.",
        "F9 aktualisiert Verweise; beim Drucken geschieht es automatisch.",
      ],
      task: {
        instruction: "Füge an einer anderen Stelle einen Querverweis auf deine Textmarke ein.",
        hint: "Einfügen → Querverweis → Verweistyp „Textmarke“ → deine Marke → Einfügen.",
        check: async () => {
          const [marken, felder] = await Promise.all([readBookmarks(), readFields()]);
          if (marken === null || felder === null) {
            return unsupported(
              "Diese Word-Version stellt dem Add-in Textmarken oder Felder nicht zur Verfügung (ab WordApi 1.4).",
              "Drück Alt + F9: Ein Verweis zeigt sich als { REF … } oder { PAGEREF … }. Danach kannst du den Schritt selbst als erledigt markieren.",
            );
          }
          const verweise = felder.filter((feld) => /^(REF|PAGEREF)\s+/i.test(feld.code));
          const aufMarke = verweise.filter((feld) =>
            marken.some((marke) => new RegExp(`^(REF|PAGEREF)\\s+${marke}\\b`, "i").test(feld.code)),
          );
          return fromCriteria(
            [
              criterion("Eine eigene Textmarke ist vorhanden", marken.length > 0),
              criterion("Ein Querverweis verweist auf diese Textmarke", aufMarke.length > 0),
            ],
            {
              success: "Der Verweis zieht nach, wenn sich Inhalt oder Seite ändern.",
              failure:
                verweise.length > 0
                  ? "Es gibt Querverweise, aber keiner zeigt auf deine Textmarke."
                  : "Es gibt noch keinen Querverweis.",
              hint: "Beim Verweistyp „Textmarke“ auswählen, nicht „Überschrift“.",
            },
          );
        },
      },
    },
  ],
};
